import { Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useBillsQuery } from '../../api/getBills';
import { getWallet } from '../../api/wallet/getWallet';
import colors from '../../constants/colors';
import useGetCustKey from '../../hooks/useGetCustKey';
import useUpdateTitle from '../../hooks/useUpdateTitle';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import AutoPayBulkEnrollCard from './components/AutoPayBulkEnrollCard';
import AutoPayCard from './components/AutoPayCard';
import CircularProgress from '../../components/CircularProgress/CircularProgress';

const AutoPayPage = () => {
  useUpdateTitle('AutoPay');
  const queryClient = useQueryClient();
  const { data: billsData, isLoading } = useBillsQuery();
  const custKey = useGetCustKey();
  const payments = billsData?.payments;

  const { closeSnackbar } = useSnackbarsContext();

  useEffect(() => {
    return () => {
      if (window.location.pathname !== '/autopay') {
        closeSnackbar();
      }
    };
  }, []);

  useEffect(() => {
    if (custKey) {
      queryClient.prefetchQuery({
        queryKey: ['wallet'],
        queryFn: () => getWallet(custKey),
      });
    }
  }, [custKey]);

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ width: 1 }}>
        <CircularProgress size={120} />
      </Stack>
    );
  }

  const allUnenrolled = payments?.every((payment) => !payment.autoPay.enrolled);

  const policiesEligibleForAutopay =
    payments?.filter((payment) => payment.autoPay.eligible) ?? [];

  const isEligibleForBulkEnroll =
    allUnenrolled && policiesEligibleForAutopay.length > 1;

  return (
    <Stack
      data-testid="autoPayPage"
      direction="column"
      spacing={{ mobile: 2, tablet: 3 }}
      sx={{
        flex: 1,
        width: 1,
        minHeight: '100%',
        backgroundColor: colors.csaaGray,
        px: { mobile: 2, tablet: 4, desktop: 7 },
        pb: 3,
      }}
    >
      <Stack
        direction="column"
        sx={{
          pt: { mobile: 2, tablet: 3, desktop: 9 },
          width: 1,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontWeight: 500,
            fontSize: { mobile: 33, tablet: 44 },
            pb: { mobile: 0.5, tablet: 1 },
            lineHeight: { mobile: '48px', desktop: '56px' },
          }}
        >
          AutoPay settings
        </Typography>
        <Typography
          sx={{
            fontSize: { mobile: 23, tablet: 28 },
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: { mobile: '32px', desktop: '40px' },
          }}
        >
          Set up and manage AutoPay. The amount due will be withdrawn on the
          payment due date.
        </Typography>
      </Stack>
      <Stack direction="column" spacing={{ mobile: 2, tablet: 3 }}>
        {isEligibleForBulkEnroll && <AutoPayBulkEnrollCard />}
        {payments?.map((payment) => {
          return (
            payment && <AutoPayCard bill={payment} key={payment.policyNumber} />
          );
        })}
      </Stack>
    </Stack>
  );
};

export default AutoPayPage;
